/**
 * Starter template definitions with full measurement philosophy configuration.
 *
 * Mirrors backend starter templates from template.py exactly.
 * 3 video templates + 3 image templates = 6 total.
 *
 * Each starter includes: gates, dimensions (from library), kpiConfig,
 * verdictRules (full rule engine v2 format), philosophy, and icon.
 */

import type { Gate, StarterTemplate, VerdictRule, DiagnosisDimension } from '@/types/template';

import { ALL_DIMENSIONS } from './diagnosis-library';

// ---------------------------------------------------------------------------
// System gates (shared across templates)
// ---------------------------------------------------------------------------

const SYSTEM_GATE_PRODUCTION_ERRORS: Gate = {
  gateId: 'gate_productionErrors',
  name: 'Production Errors',
  instructions:
    'Evaluate: Technical production quality of the video creative.\n' +
    'PASS when: No visible glitches, sync issues, resolution problems, or technical artifacts\n' +
    'FAIL when: Any glitch, audio sync issue, resolution drop, or technical artifact is visible',
  role: 'critical',
  isSystem: true,
};

const SYSTEM_GATE_FORMAT_HYGIENE: Gate = {
  gateId: 'gate_formatHygiene',
  name: 'Format Hygiene',
  instructions:
    'Evaluate: Video aspect ratio matches declared placement AND respects safe zones.\n' +
    'PASS when: MEASURED dimensions confirm aspect ratio matches placement ' +
    '(Reels/Stories=9:16 vertical, Feed=1:1 or 16:9) ' +
    'and text/logos are within safe zones\n' +
    'FAIL when: MEASURED dimensions show aspect ratio mismatches placement ' +
    'or content extends beyond safe zones. ' +
    'IMPORTANT: Use the Video Dimensions section (if provided) for aspect ratio -- ' +
    'do NOT visually estimate.',
  role: 'critical',
  isSystem: true,
};

const IMAGE_SYSTEM_GATE_PRODUCTION_ERRORS: Gate = {
  gateId: 'gate_productionErrors',
  name: 'Production Errors',
  instructions:
    'Evaluate: Technical production quality of the image creative.\n' +
    'PASS when: No visible compression artifacts, resolution problems, ' +
    'rendering glitches, or technical defects\n' +
    'FAIL when: Any compression artifact, resolution issue, rendering glitch, ' +
    'or technical defect is visible',
  role: 'critical',
  isSystem: true,
};

const IMAGE_SYSTEM_GATE_FORMAT_HYGIENE: Gate = {
  gateId: 'gate_formatHygiene',
  name: 'Format Hygiene',
  instructions:
    'Evaluate: Image aspect ratio matches declared placement AND meets minimum resolution.\n' +
    'PASS when: MEASURED dimensions confirm aspect ratio matches placement ' +
    '(Feed=1:1 or 4:5, Stories/Reels=9:16) ' +
    'and resolution is at least 600x600px\n' +
    'FAIL when: MEASURED dimensions show aspect ratio mismatches placement ' +
    'or resolution is below minimum. ' +
    'IMPORTANT: Use the Image Dimensions section (if provided) for aspect ratio -- ' +
    'do NOT visually estimate.',
  role: 'critical',
  isSystem: true,
};

// ---------------------------------------------------------------------------
// Standard verdict rules v2 (used by Standard and Custom)
// ---------------------------------------------------------------------------

function standardVerdictRulesV2(): VerdictRule[] {
  return [
    {
      id: 'std-r1',
      priority: 1,
      verdict: 'Kill',
      label: 'Critical quality failure',
      groupsLogic: 'OR',
      conditionGroups: [
        {
          id: 'std-r1-g1',
          logic: 'AND',
          conditions: [
            {
              id: 'std-r1-c1',
              source: { type: 'gateAggregate', aggregate: 'any', role: 'critical' },
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    },
    {
      id: 'std-r2',
      priority: 2,
      verdict: 'Fix',
      label: 'Standard gate failures to address',
      groupsLogic: 'OR',
      conditionGroups: [
        {
          id: 'std-r2-g1',
          logic: 'AND',
          conditions: [
            {
              id: 'std-r2-c1',
              source: { type: 'gateAggregate', aggregate: 'any', role: 'standard' },
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    },
    {
      id: 'std-r3',
      priority: 3,
      verdict: 'Scale',
      label: 'All gates pass',
      groupsLogic: 'AND',
      conditionGroups: [],
    },
  ];
}

// ---------------------------------------------------------------------------
// Direct Response verdict rules v2
// ---------------------------------------------------------------------------

function directResponseVerdictRulesV2(): VerdictRule[] {
  return [
    {
      id: 'dr-r1',
      priority: 1,
      verdict: 'Kill',
      label: 'Critical quality failure',
      groupsLogic: 'OR',
      conditionGroups: [
        {
          id: 'dr-r1-g1',
          logic: 'AND',
          conditions: [
            {
              id: 'dr-r1-c1',
              source: { type: 'gateAggregate', aggregate: 'any', role: 'critical' },
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    },
    {
      id: 'dr-r2',
      priority: 2,
      verdict: 'Fix',
      label: 'Standard gate failures to address',
      groupsLogic: 'OR',
      conditionGroups: [
        {
          id: 'dr-r2-g1',
          logic: 'AND',
          conditions: [
            {
              id: 'dr-r2-c1',
              source: { type: 'gateAggregate', aggregate: 'any', role: 'standard' },
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    },
    {
      id: 'dr-r3',
      priority: 3,
      verdict: 'Scale',
      label: 'All gates pass',
      groupsLogic: 'AND',
      conditionGroups: [],
    },
  ];
}

// ---------------------------------------------------------------------------
// Production QA verdict rules v2
// ---------------------------------------------------------------------------

function productionQaVerdictRulesV2(): VerdictRule[] {
  return [
    {
      id: 'qa-r1',
      priority: 1,
      verdict: 'Kill',
      label: 'Fails technical QA — do not publish',
      groupsLogic: 'OR',
      conditionGroups: [
        {
          id: 'qa-r1-g1',
          logic: 'AND',
          conditions: [
            {
              id: 'qa-r1-c1',
              source: { type: 'gateAggregate', aggregate: 'any' },
              operator: 'fails',
              value: true,
            },
          ],
        },
      ],
    },
    {
      id: 'qa-r2',
      priority: 2,
      verdict: 'Scale',
      label: 'Passes all QA checks — cleared for publishing',
      groupsLogic: 'AND',
      conditionGroups: [],
    },
  ];
}

// ---------------------------------------------------------------------------
// Helper: resolve dimension IDs from library
// ---------------------------------------------------------------------------

function dims(...ids: string[]): DiagnosisDimension[] {
  return ids.map((id) => {
    const dim = ALL_DIMENSIONS[id];
    if (!dim) {
      throw new Error(`Dimension ${id} not found in library`);
    }
    // Return a copy without frameworks (not stored on template instances)
    return { ...dim };
  });
}

// ---------------------------------------------------------------------------
// Starter template dimension lists (matching backend exactly)
// ---------------------------------------------------------------------------

function standardDimensions(): DiagnosisDimension[] {
  return dims(
    'dim_cepMentalAvailability',
    'dim_brandLinkage',
    'dim_emotionalRegister',
    'dim_narrativeStructure',
    'dim_distinctiveness',
    'dim_messageRetention',
    'dim_targetAlignment',
  );
}

function directResponseDimensions(): DiagnosisDimension[] {
  return dims(
    'dim_hookEffectiveness',
    'dim_offerClarity',
    'dim_activationUrgency',
    'dim_socialProof',
    'dim_targetAlignment',
  );
}

function productionQaDimensions(): DiagnosisDimension[] {
  return dims('dim_audioClarity', 'dim_visualCoherence');
}

function imageStandardDimensions(): DiagnosisDimension[] {
  return dims(
    'dim_brandLinkage',
    'dim_emotionalRegister',
    'dim_distinctiveness',
    'dim_visualHierarchy',
    'dim_compositionLayout',
    'dim_messageRetention',
  );
}

function imageDirectResponseDimensions(): DiagnosisDimension[] {
  return dims(
    'dim_hookEffectiveness',
    'dim_offerClarity',
    'dim_activationUrgency',
    'dim_socialProof',
    'dim_visualHierarchy',
  );
}

function imageProductionQaDimensions(): DiagnosisDimension[] {
  return dims('dim_visualCoherence', 'dim_compositionLayout');
}

// ===========================================================================
// VIDEO STARTER TEMPLATES
// ===========================================================================

const STANDARD: StarterTemplate = {
  key: 'standard',
  name: 'AdVerdict Standard (Ehrenberg-Bass)',
  philosophy: 'Long-term brand equity over short-term activation',
  description:
    'The default evaluation framework based on Ehrenberg-Bass Institute ' +
    'principles of creative effectiveness. Covers production quality, ' +
    'format compliance, brand timing, messaging clarity, product ' +
    'visibility, call-to-action, and brand color usage.',
  icon: 'Shield',
  mediaType: 'video',
  gates: [
    SYSTEM_GATE_PRODUCTION_ERRORS,
    SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_brandTiming',
      name: 'Brand Timing',
      instructions:
        'Evaluate: Whether a brand cue appears within the first 2 seconds of the video.\n' +
        'PASS when: A brand element (logo, brand color, product, tagline) is clearly ' +
        'visible within the first 2 seconds\n' +
        'FAIL when: No brand cue appears in the first 2 seconds of the video',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_clearClaim',
      name: 'Clear Claim',
      instructions:
        'Evaluate: Whether the video contains one identifiable message or claim.\n' +
        'PASS when: A single clear value proposition, benefit, or message is communicated\n' +
        'FAIL when: No identifiable claim, or multiple competing messages that dilute clarity',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_productShown',
      name: 'Product Shown',
      instructions:
        'Evaluate: Whether the product appears clearly in the video.\n' +
        'PASS when: The product is shown clearly at least once during the video\n' +
        'FAIL when: The product is never shown or is only partially/ambiguously visible',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_ctaPresent',
      name: 'CTA Present',
      instructions:
        'Evaluate: Whether an active, placement-appropriate call-to-action is present.\n' +
        "PASS when: A specific actionable directive is present — such as 'Shop Now', " +
        "'Learn More', 'Try Free', a button graphic, or a clear verbal CTA. The CTA " +
        'must be readable or audible.\n' +
        'FAIL when: The only CTA is a raw URL, a QR code alone, or a passive brand ' +
        'mention with no directive. For Reels/Stories, the CTA must be visible outside ' +
        'the bottom UI safe zone (bottom 20% is covered by the platform interface). ' +
        'Also FAIL if no CTA of any kind is present.',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_brandColorsUsed',
      name: 'Brand Colors Used',
      instructions:
        "Evaluate: Whether the video uses the brand's primary colors.\n" +
        "PASS when: The brand's primary colors are prominently used throughout the video\n" +
        'FAIL when: Different colors dominate or brand colors are absent from the video',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_cepLinkage',
      name: 'CEP Linkage',
      instructions:
        'Evaluate: The ad triggers at least one recognisable buying occasion for this ' +
        'product category.\n' +
        'PASS when: The ad connects the brand to a specific moment, need state, or ' +
        'context that prompts category purchase — a buying occasion a category buyer ' +
        "would recognise ('running low on food', 'want the best for my dog', 'heading " +
        "outdoors'). The linkage can be visual, verbal, or contextual.\n" +
        'FAIL when: The ad is purely aesthetic or lifestyle imagery with no discernible ' +
        'link to a category buying moment. Beautiful photography that could be for any ' +
        'brand in any category does not pass. Evaluate against the category buying ' +
        'context, not just the brand.',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: standardDimensions(),
  kpiConfig: {
    mode: 'auto',
    primaryKpiId: null,
    secondaryKpiIds: [],
    benchmark: null,
  },
  verdictRules: standardVerdictRulesV2(),
  defaultVerdict: 'Scale',
};

const DIRECT_RESPONSE: StarterTemplate = {
  key: 'direct_response',
  name: 'Direct Response',
  philosophy: 'Immediate conversion over long-term brand building',
  description:
    'Performance-focused evaluation for direct response and conversion-oriented ' +
    'creatives. Emphasizes scroll-stopping hooks, offer clarity, CTA prominence, ' +
    'and urgency tactics alongside production fundamentals.',
  icon: 'Zap',
  mediaType: 'video',
  gates: [
    SYSTEM_GATE_PRODUCTION_ERRORS,
    SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_hookStrength',
      name: 'Hook Strength',
      instructions:
        'Evaluate: Whether the opening stops the scroll and grabs attention.\n' +
        'PASS when: First 1-2 seconds contain a visually striking element, bold text ' +
        'hook, or pattern interrupt that demands attention\n' +
        'FAIL when: Opening is generic, starts with a logo reveal, or has no ' +
        'differentiated hook to stop the scroll',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_offerClarity',
      name: 'Offer Clarity',
      instructions:
        'Evaluate: Whether the offer or value proposition is clearly communicated.\n' +
        'PASS when: The viewer can immediately understand what is being offered, ' +
        'the price or value, and why they should care\n' +
        'FAIL when: The offer is buried, unclear, or requires multiple viewings ' +
        'to understand what is being sold',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_urgencyTactics',
      name: 'Urgency Tactics',
      instructions:
        'Evaluate: Whether urgency or scarcity elements are present to drive action.\n' +
        'PASS when: The creative includes time-limited offers, scarcity signals, ' +
        'countdown elements, or urgency-driving language\n' +
        'FAIL when: No urgency or scarcity elements are present to motivate ' +
        'immediate action',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_ctaProminence',
      name: 'CTA Prominence',
      instructions:
        'Evaluate: Whether the call-to-action is visually prominent and well-timed.\n' +
        'PASS when: CTA is large, high-contrast, appears at the right moment, ' +
        'and clearly tells the viewer what to do next\n' +
        'FAIL when: CTA is small, low-contrast, poorly timed, or blends into ' +
        'the background',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: directResponseDimensions(),
  kpiConfig: {
    mode: 'auto',
    primaryKpiId: null,
    secondaryKpiIds: [],
    benchmark: null,
  },
  verdictRules: directResponseVerdictRulesV2(),
  defaultVerdict: 'Scale',
};

const PRODUCTION_QA: StarterTemplate = {
  key: 'production_qa',
  name: 'Production QA',
  philosophy: 'Technical pass/fail before spend — catch errors before they cost money',
  description:
    'Quality-focused evaluation for production review. Emphasizes audio clarity, ' +
    'visual polish, text readability, and transition quality alongside production ' +
    'fundamentals.',
  icon: 'CheckCircle',
  mediaType: 'video',
  gates: [
    SYSTEM_GATE_PRODUCTION_ERRORS,
    SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_audioQuality',
      name: 'Audio Quality',
      instructions:
        'Evaluate: Audio levels, clarity, and absence of distortion in the video.\n' +
        'PASS when: Audio is clear, properly leveled, free from distortion, ' +
        'clipping, or background noise that impairs comprehension\n' +
        'FAIL when: Audio has distortion, clipping, uneven levels, distracting ' +
        'background noise, or is unintelligible',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_visualPolish',
      name: 'Visual Polish',
      instructions:
        'Evaluate: Color grading, lighting, and focus consistency throughout the video.\n' +
        'PASS when: Color grading is consistent, lighting is appropriate, and focus ' +
        'is maintained throughout the creative\n' +
        'FAIL when: Inconsistent color grading, poor lighting, out-of-focus shots, ' +
        'or visible quality degradation',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_textReadability',
      name: 'Text Readability',
      instructions:
        'Evaluate: On-screen text size, display duration, and contrast for readability.\n' +
        'PASS when: All on-screen text is large enough to read, displayed long enough ' +
        'to comprehend, and has sufficient contrast against the background\n' +
        'FAIL when: Text is too small, disappears too quickly, or lacks contrast ' +
        'making it difficult to read',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_transitionSmooth',
      name: 'Transition Smoothness',
      instructions:
        'Evaluate: Cut quality, transition timing, and visual flow between scenes.\n' +
        'PASS when: Scene transitions are clean, well-timed, and maintain visual ' +
        'continuity without jarring jumps\n' +
        'FAIL when: Transitions are abrupt, poorly timed, cause visual discontinuity, ' +
        'or disrupt the viewing flow',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: productionQaDimensions(),
  kpiConfig: null,
  verdictRules: productionQaVerdictRulesV2(),
  defaultVerdict: 'Kill',
};

// ===========================================================================
// IMAGE STARTER TEMPLATES
// ===========================================================================

const IMAGE_STANDARD: StarterTemplate = {
  key: 'standard_image',
  name: 'AdVerdict Standard (Ehrenberg-Bass) -- Image',
  philosophy: 'Long-term brand equity over short-term activation',
  description:
    'The default image evaluation framework based on Ehrenberg-Bass Institute ' +
    'principles of creative effectiveness. Covers production quality, format ' +
    'compliance, brand prominence, messaging clarity, product presentation, ' +
    'call-to-action presence, and distinctive assets.',
  icon: 'Shield',
  mediaType: 'image',
  gates: [
    IMAGE_SYSTEM_GATE_PRODUCTION_ERRORS,
    IMAGE_SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_brandProminence',
      name: 'Brand Prominence',
      instructions:
        'Evaluate: Whether brand elements are prominently visible in the image.\n' +
        'PASS when: A brand element (logo, brand color, product, tagline) is clearly ' +
        'visible and occupies a prominent position in the composition\n' +
        'FAIL when: No brand cue is visible or brand elements are too small, ' +
        'obscured, or positioned where they are easily missed',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_messageClarity',
      name: 'Message Clarity',
      instructions:
        'Evaluate: Whether the image communicates one identifiable message or claim.\n' +
        'PASS when: A single clear value proposition, benefit, or message is ' +
        'communicated through text and/or visual hierarchy\n' +
        'FAIL when: No identifiable claim, or multiple competing messages that ' +
        'dilute clarity and confuse the viewer',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_productPresentation',
      name: 'Product Presentation',
      instructions:
        'Evaluate: Whether the product appears clearly in the image.\n' +
        'PASS when: The product is shown clearly, well-lit, and occupies enough ' +
        'visual space to be immediately recognizable\n' +
        'FAIL when: The product is absent, partially obscured, too small to ' +
        'identify, or poorly lit/angled',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_ctaPresence',
      name: 'CTA Presence',
      instructions:
        'Evaluate: Whether an active, placement-appropriate call-to-action is visible.\n' +
        "PASS when: A specific actionable directive is present — such as 'Shop Now', " +
        "'Learn More', 'Try Free', a button graphic, or a text directive. The CTA must " +
        'be readable and visually distinguishable from the background.\n' +
        'FAIL when: The only CTA is a raw URL, a QR code alone, or a passive brand ' +
        'mention with no directive. For Stories/Reels, the CTA must be visible outside ' +
        'the bottom UI safe zone (bottom 20% is covered by the platform interface). ' +
        'Also FAIL if no CTA of any kind is present or all CTA elements are illegible.',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_distinctiveAssets',
      name: 'Distinctive Assets',
      instructions:
        "Evaluate: Whether the brand's distinctive visual assets are used.\n" +
        "PASS when: The brand's primary colors, typography, iconography, or " +
        'other distinctive elements are prominently featured\n' +
        'FAIL when: Different colors dominate, no recognizable brand elements ' +
        'are present, or the creative could belong to any brand',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_cepLinkage',
      name: 'CEP Linkage',
      instructions:
        'Evaluate: The image triggers at least one recognisable buying occasion for ' +
        'this product category.\n' +
        'PASS when: The image connects the brand to a specific moment, need state, or ' +
        'context that prompts category purchase — a buying occasion a category buyer ' +
        "would recognise ('running low on food', 'want the best for my dog', 'heading " +
        "outdoors'). The linkage can be visual or contextual.\n" +
        'FAIL when: The image is purely aesthetic or lifestyle imagery with no ' +
        'discernible link to a category buying moment. Beautiful photography that ' +
        'could be for any brand in any category does not pass. Evaluate against the ' +
        'category buying context, not just the brand.',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: imageStandardDimensions(),
  kpiConfig: {
    mode: 'auto',
    primaryKpiId: null,
    secondaryKpiIds: [],
    benchmark: null,
  },
  verdictRules: standardVerdictRulesV2(),
  defaultVerdict: 'Scale',
};

const IMAGE_DIRECT_RESPONSE: StarterTemplate = {
  key: 'direct_response_image',
  name: 'Direct Response -- Image',
  philosophy: 'Immediate conversion over long-term brand building',
  description:
    'Performance-focused evaluation for direct response image creatives. ' +
    'Emphasizes scroll-stopping hooks, offer clarity, CTA prominence, ' +
    'and urgency tactics alongside production fundamentals.',
  icon: 'Zap',
  mediaType: 'image',
  gates: [
    IMAGE_SYSTEM_GATE_PRODUCTION_ERRORS,
    IMAGE_SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_hookStrength',
      name: 'Hook Strength',
      instructions:
        'Evaluate: Whether the image stops the scroll and grabs attention.\n' +
        'PASS when: The image contains a visually striking element, bold text ' +
        'hook, or pattern interrupt that demands immediate attention\n' +
        'FAIL when: The image is generic, unremarkable, or has no ' +
        'differentiated visual hook to stop the scroll',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_offerClarity',
      name: 'Offer Clarity',
      instructions:
        'Evaluate: Whether the offer or value proposition is clearly communicated.\n' +
        'PASS when: The viewer can immediately understand what is being offered, ' +
        'the price or value, and why they should care\n' +
        'FAIL when: The offer is buried, unclear, or requires extended study ' +
        'to understand what is being sold',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_ctaProminence',
      name: 'CTA Prominence',
      instructions:
        'Evaluate: Whether the call-to-action is visually prominent.\n' +
        'PASS when: CTA is large, high-contrast, well-positioned, ' +
        'and clearly tells the viewer what to do next\n' +
        'FAIL when: CTA is small, low-contrast, poorly positioned, or blends ' +
        'into the background',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_urgencyTactics',
      name: 'Urgency Tactics',
      instructions:
        'Evaluate: Whether urgency or scarcity elements are present to drive action.\n' +
        'PASS when: The image includes time-limited offers, scarcity signals, ' +
        'countdown elements, or urgency-driving language\n' +
        'FAIL when: No urgency or scarcity elements are present to motivate ' +
        'immediate action',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: imageDirectResponseDimensions(),
  kpiConfig: {
    mode: 'auto',
    primaryKpiId: null,
    secondaryKpiIds: [],
    benchmark: null,
  },
  verdictRules: directResponseVerdictRulesV2(),
  defaultVerdict: 'Scale',
};

const IMAGE_PRODUCTION_QA: StarterTemplate = {
  key: 'production_qa_image',
  name: 'Production QA -- Image',
  philosophy: 'Technical pass/fail before spend — catch errors before they cost money',
  description:
    'Quality-focused evaluation for image production review. Emphasizes ' +
    'image resolution, text readability, color accuracy, and layout balance ' +
    'alongside production fundamentals.',
  icon: 'CheckCircle',
  mediaType: 'image',
  gates: [
    IMAGE_SYSTEM_GATE_PRODUCTION_ERRORS,
    IMAGE_SYSTEM_GATE_FORMAT_HYGIENE,
    {
      gateId: 'gate_imageResolution',
      name: 'Image Resolution',
      instructions:
        'Evaluate: Whether the image meets minimum resolution requirements.\n' +
        'PASS when: The image is sharp, clear, and free from visible pixelation, ' +
        'upscaling artifacts, or blur at the intended display size\n' +
        'FAIL when: The image shows pixelation, upscaling artifacts, blur, or ' +
        'any signs of insufficient resolution for the target placement',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_textReadability',
      name: 'Text Readability',
      instructions:
        'Evaluate: Whether on-screen text is readable at the intended display size.\n' +
        'PASS when: All text is large enough to read, has sufficient contrast ' +
        'against the background, and uses legible typography\n' +
        'FAIL when: Text is too small, lacks contrast, uses hard-to-read fonts, ' +
        'or is positioned over busy backgrounds that impair readability',
      role: 'critical',
      isSystem: false,
    },
    {
      gateId: 'gate_colorAccuracy',
      name: 'Color Accuracy',
      instructions:
        'Evaluate: Whether colors are accurate and consistent with brand guidelines.\n' +
        'PASS when: Colors appear natural, brand colors match guidelines, and ' +
        'no color casts, banding, or unintended shifts are visible\n' +
        'FAIL when: Colors appear unnatural, brand colors are off-spec, or ' +
        'visible color banding, casts, or shifts are present',
      role: 'standard',
      isSystem: false,
    },
    {
      gateId: 'gate_layoutBalance',
      name: 'Layout Balance',
      instructions:
        'Evaluate: Whether the visual layout is balanced and well-composed.\n' +
        'PASS when: Elements are well-distributed, visual hierarchy is clear, ' +
        'and the composition guides the eye naturally through the content\n' +
        'FAIL when: The layout feels cramped, unbalanced, or cluttered, with ' +
        'no clear visual hierarchy or focal point',
      role: 'standard',
      isSystem: false,
    },
  ],
  dimensions: imageProductionQaDimensions(),
  kpiConfig: null,
  verdictRules: productionQaVerdictRulesV2(),
  defaultVerdict: 'Kill',
};

// ===========================================================================
// EXPORTED ARRAYS
// ===========================================================================

/** Video starter templates */
export const STARTER_TEMPLATES: StarterTemplate[] = [
  STANDARD,
  DIRECT_RESPONSE,
  PRODUCTION_QA,
];

/** Image starter templates */
export const IMAGE_STARTER_TEMPLATES: StarterTemplate[] = [
  IMAGE_STANDARD,
  IMAGE_DIRECT_RESPONSE,
  IMAGE_PRODUCTION_QA,
];

/** All starter templates — unified (3 starters, no media type separation) */
export const ALL_STARTER_TEMPLATES: StarterTemplate[] = [
  ...STARTER_TEMPLATES,
];
